import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Pencil, Plus, Search, Tag as TagIcon, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/utils';
import {
  ITEM_TAG_COLORS,
  useCreateItemTag,
  useDeleteItemTag,
  useItemTagCatalog,
  useMergeItemTags,
  useSetItemTags,
  useTagsForItem,
  useUpdateItemTag,
  type ItemTag,
  type ItemTagColor,
} from '@/features/inventory/hooks';

/**
 * Tag picker for the item detail page.
 *
 * Renders the currently-attached tags as colour-coded pills and, for
 * editors, a "+ Add" affordance that opens a dropdown with:
 *
 *   * a search box that filters the org's tag catalog,
 *   * existing tags as toggleable rows (click to attach / detach),
 *   * an inline "Create new tag" button when the search box doesn't
 *     match anything — the user picks a colour swatch, hits Enter,
 *     and the tag is both created in the catalog AND attached to the
 *     current item in one action.
 *
 * Read-only viewers see the same pills minus the controls.
 *
 * The component is fully self-contained — give it an `itemId` and a
 * `canEdit` flag and it owns its own queries / mutations against the
 * `item_tags` + `item_tag_assignments` tables.
 */
export function TagsPicker({
  itemId,
  canEdit,
  className,
}: {
  itemId: string;
  canEdit: boolean;
  className?: string;
}) {
  const { data: assigned, isLoading: assignedLoading } = useTagsForItem(itemId);
  const { data: catalog, isLoading: catalogLoading } = useItemTagCatalog();
  const setItemTags = useSetItemTags();
  const createTag = useCreateItemTag();
  const deleteTag = useDeleteItemTag();
  const updateTag = useUpdateItemTag();
  const mergeTags = useMergeItemTags();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draftColor, setDraftColor] = useState<ItemTagColor>('slate');
  // Inline rename state: which catalog row is currently in edit mode
  // and the in-progress draft name. Kept on the parent so that
  // re-rendering the row list doesn't lose focus / draft state.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const assignedIds = useMemo(() => new Set((assigned ?? []).map((t) => t.id)), [assigned]);

  // Reset the dropdown state every time it re-opens so a stale query
  // / colour choice doesn't bleed across edits.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDraftColor(pickNextColor(catalog ?? []));
      setRenamingId(null);
      setDraftName('');
      // The autofocus prop on the input doesn't fire reliably inside
      // the Radix Dropdown portal on first open; explicitly nudge it.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open, catalog]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog ?? [];
    return (catalog ?? []).filter((t) => t.name.toLowerCase().includes(q));
  }, [catalog, query]);

  // True when the typed name doesn't (case-insensitively) match any
  // tag in the catalog. Drives the "Create new tag" affordance.
  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return (catalog ?? []).find((t) => t.name.toLowerCase() === q) ?? null;
  }, [catalog, query]);

  const trimmedQuery = query.trim();
  const showCreate = canEdit && trimmedQuery.length > 0 && !exactMatch;

  const toggle = async (tag: ItemTag) => {
    const currentIds = (assigned ?? []).map((t) => t.id);
    const next = new Set(currentIds);
    if (next.has(tag.id)) next.delete(tag.id);
    else next.add(tag.id);
    try {
      await setItemTags.mutateAsync({
        itemId,
        tagIds: Array.from(next),
        currentTagIds: currentIds,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update tags.');
    }
  };

  const removeAssigned = async (tagId: string) => {
    const currentIds = (assigned ?? []).map((t) => t.id);
    if (!currentIds.includes(tagId)) return;
    try {
      await setItemTags.mutateAsync({
        itemId,
        tagIds: currentIds.filter((id) => id !== tagId),
        currentTagIds: currentIds,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove tag.');
    }
  };

  const createAndAttach = async () => {
    const name = trimmedQuery;
    if (!name) return;
    try {
      const tag = await createTag.mutateAsync({ name, color: draftColor });
      const currentIds = (assigned ?? []).map((t) => t.id);
      if (!currentIds.includes(tag.id)) {
        await setItemTags.mutateAsync({
          itemId,
          tagIds: [...currentIds, tag.id],
          currentTagIds: currentIds,
        });
      }
      setQuery('');
      setDraftColor(pickNextColor([...(catalog ?? []), tag]));
      // Keep the dropdown open so the operator can keep adding tags
      // without re-clicking the trigger.
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create tag.');
    }
  };

  const removeFromCatalog = async (tag: ItemTag) => {
    const ok = confirm(
      `Delete the “${tag.name}” tag for the whole organisation? It will be removed from every item that uses it.`,
    );
    if (!ok) return;
    try {
      await deleteTag.mutateAsync({ id: tag.id });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete tag.');
    }
  };

  const recolour = async (tag: ItemTag, color: ItemTagColor) => {
    if (tag.color === color) return;
    try {
      await updateTag.mutateAsync({ id: tag.id, patch: { color } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not recolour tag.');
    }
  };

  const startRename = (tag: ItemTag) => {
    setRenamingId(tag.id);
    setDraftName(tag.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setDraftName('');
  };

  /**
   * Commit the in-progress rename for `tag`. Three branches:
   *
   *   1. Empty / unchanged name → no-op exit.
   *   2. Case-insensitive match against another catalog row → prompt
   *      to merge instead of attempting the rename. The DB's
   *      case-insensitive unique index would refuse a straight
   *      rename here anyway (returning a generic 23505 error), so
   *      detecting the collision client-side lets us offer the
   *      operator the useful action: consolidate the two duplicate
   *      tags into the one they were aiming at, dragging every
   *      affected item along with them.
   *   3. Otherwise straight rename via `useUpdateItemTag`. The DB
   *      stores assignments by `tag_id`, so every item that carries
   *      this tag automatically picks up the new name on its next
   *      fetch — the catalog + tags-for-item caches are invalidated
   *      by the hook so the visible UI updates immediately too.
   */
  const commitRename = async (tag: ItemTag) => {
    const next = draftName.trim();
    if (!next || next === tag.name) {
      cancelRename();
      return;
    }

    const collision = (catalog ?? []).find(
      (t) => t.id !== tag.id && t.name.toLowerCase() === next.toLowerCase(),
    );
    if (collision) {
      const ok = confirm(
        `A tag named “${collision.name}” already exists. ` +
          `Merge “${tag.name}” into it? Every item currently tagged ` +
          `“${tag.name}” will be re-tagged as “${collision.name}”, ` +
          `and the “${tag.name}” tag will be removed.`,
      );
      if (!ok) {
        // Leave the operator in edit mode so they can pick a
        // different name without retyping.
        return;
      }
      try {
        await mergeTags.mutateAsync({ sourceId: tag.id, targetId: collision.id });
        toast.success(`Merged “${tag.name}” into “${collision.name}”.`);
        cancelRename();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not merge tags.');
      }
      return;
    }

    try {
      await updateTag.mutateAsync({ id: tag.id, patch: { name: next } });
      cancelRename();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not rename tag.');
    }
  };

  // ---------------- Read-only render ----------------
  if (!canEdit) {
    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {assignedLoading ? (
          <span className="text-xs text-muted-foreground">…</span>
        ) : (assigned ?? []).length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          (assigned ?? []).map((t) => <TagBadge key={t.id} tag={t} />)
        )}
      </div>
    );
  }

  // ---------------- Editable render ----------------
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {(assigned ?? []).map((t) => (
        <span key={t.id} className="inline-flex items-center">
          <TagBadge tag={t} />
          <button
            type="button"
            onClick={() => void removeAssigned(t.id)}
            className="-ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={`Remove ${t.name} tag`}
            title={`Remove ${t.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-secondary/60 hover:text-foreground"
            aria-label="Add tag"
          >
            <Plus className="h-3 w-3" />
            <span>{(assigned ?? []).length === 0 ? 'Add tags' : 'Add'}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-0">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                // Enter on a non-matching query commits a "create new
                // tag" without forcing the user to take their hands
                // off the keyboard.
                if (e.key === 'Enter' && showCreate) {
                  e.preventDefault();
                  void createAndAttach();
                }
              }}
              placeholder="Search or create…"
              maxLength={64}
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {catalogLoading ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 && !showCreate ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No tags yet — start typing to create one.
              </div>
            ) : (
              filtered.map((tag) => {
                const checked = assignedIds.has(tag.id);
                const isRenaming = renamingId === tag.id;
                const renameBusy = updateTag.isPending || mergeTags.isPending;
                return (
                  <div
                    key={tag.id}
                    className="group flex w-full items-center gap-2 rounded-md px-2 py-1 hover:bg-secondary/60"
                  >
                    {isRenaming ? (
                      <div className="flex min-w-0 flex-1 items-center gap-1">
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitRename(tag);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          maxLength={64}
                          disabled={renameBusy}
                          aria-label={`Rename ${tag.name} tag`}
                          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-0.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                        />
                        <button
                          type="button"
                          onClick={() => void commitRename(tag)}
                          disabled={renameBusy}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-60"
                          aria-label="Save rename"
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelRename}
                          disabled={renameBusy}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-60"
                          aria-label="Cancel rename"
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => void toggle(tag)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border',
                              checked
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'opacity-60',
                            )}
                          >
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <TagBadge tag={tag} />
                        </button>
                        <button
                          type="button"
                          onClick={() => startRename(tag)}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground group-hover:opacity-100"
                          aria-label={`Rename ${tag.name} tag`}
                          title="Rename tag"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <ColorMenu value={tag.color} onChange={(c) => void recolour(tag, c)} />
                        <button
                          type="button"
                          onClick={() => void removeFromCatalog(tag)}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                          aria-label={`Delete ${tag.name} tag`}
                          title="Delete tag"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {showCreate && (
            <div className="border-t border-border p-2">
              <p className="px-1 pb-2 text-xs text-muted-foreground">Create new tag</p>
              <button
                type="button"
                onClick={() => void createAndAttach()}
                disabled={createTag.isPending || setItemTags.isPending}
                className="flex w-full items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60 disabled:opacity-60"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate font-medium">{trimmedQuery}</span>
                </span>
                <TagSwatch color={draftColor} />
              </button>
              <div className="mt-2 flex flex-wrap items-center gap-1.5 px-1">
                {ITEM_TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setDraftColor(c)}
                    aria-label={`Use ${c} colour`}
                    title={c}
                    className={cn(
                      'h-5 w-5 rounded-full border-2 transition-transform hover:scale-110',
                      colorSwatchClass(c),
                      draftColor === c ? 'border-foreground' : 'border-transparent',
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// =====================================================================
// Sub-components
// =====================================================================

/**
 * Coloured tag badge. Used in the displayed list, inside the dropdown
 * row, and inside the trigger of the "Create new tag" preview.
 */
function TagBadge({ tag }: { tag: ItemTag }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        colorBadgeClass(tag.color),
      )}
    >
      <TagIcon className="h-2.5 w-2.5 opacity-70" aria-hidden />
      <span className="max-w-[12rem] truncate">{tag.name}</span>
    </span>
  );
}

/**
 * Compact swatch used inside the "create new tag" preview button so
 * the operator can see the chosen colour at a glance without staring
 * at the active swatch in the picker grid below.
 */
function TagSwatch({ color }: { color: ItemTagColor }) {
  return (
    <span
      className={cn(
        'inline-block h-3.5 w-3.5 shrink-0 rounded-full border border-foreground/10',
        colorSwatchClass(color),
      )}
    />
  );
}

/**
 * Small popover that lets editors recolour an existing tag without
 * opening a dialog. Renders a compact grid of swatches; clicking one
 * fires `onChange` and closes the menu.
 */
function ColorMenu({
  value,
  onChange,
}: {
  value: ItemTagColor;
  onChange: (next: ItemTagColor) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-transparent transition-colors hover:border-border hover:bg-secondary"
          aria-label="Change tag colour"
          title="Change colour"
        >
          <TagSwatch color={value} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="grid grid-cols-5 gap-1 p-2">
        {ITEM_TAG_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              onChange(c);
              setOpen(false);
            }}
            aria-label={`Use ${c} colour`}
            title={c}
            className={cn(
              'h-5 w-5 rounded-full border-2 transition-transform hover:scale-110',
              colorSwatchClass(c),
              value === c ? 'border-foreground' : 'border-transparent',
            )}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// =====================================================================
// Colour token → Tailwind class maps
//
// Keep these in lockstep with the CHECK constraint on
// `public.item_tags.color` (migration 40). Tailwind needs literal
// classnames in the source for its JIT to pick them up, so we list
// the full set here rather than computing them.
// =====================================================================

function colorBadgeClass(color: ItemTagColor): string {
  switch (color) {
    case 'slate':
      return 'border-slate-200 bg-slate-100 text-slate-700';
    case 'red':
      return 'border-red-200 bg-red-100 text-red-700';
    case 'orange':
      return 'border-orange-200 bg-orange-100 text-orange-700';
    case 'amber':
      return 'border-amber-200 bg-amber-100 text-amber-800';
    case 'green':
      return 'border-green-200 bg-green-100 text-green-700';
    case 'teal':
      return 'border-teal-200 bg-teal-100 text-teal-700';
    case 'blue':
      return 'border-blue-200 bg-blue-100 text-blue-700';
    case 'indigo':
      return 'border-indigo-200 bg-indigo-100 text-indigo-700';
    case 'purple':
      return 'border-purple-200 bg-purple-100 text-purple-700';
    case 'pink':
      return 'border-pink-200 bg-pink-100 text-pink-700';
    default:
      return 'border-slate-200 bg-slate-100 text-slate-700';
  }
}

function colorSwatchClass(color: ItemTagColor): string {
  switch (color) {
    case 'slate':
      return 'bg-slate-400';
    case 'red':
      return 'bg-red-400';
    case 'orange':
      return 'bg-orange-400';
    case 'amber':
      return 'bg-amber-400';
    case 'green':
      return 'bg-green-400';
    case 'teal':
      return 'bg-teal-400';
    case 'blue':
      return 'bg-blue-400';
    case 'indigo':
      return 'bg-indigo-400';
    case 'purple':
      return 'bg-purple-400';
    case 'pink':
      return 'bg-pink-400';
    default:
      return 'bg-slate-400';
  }
}

/**
 * Pick a default colour for the "create new tag" form so the user
 * isn't always staring at the same swatch. We rotate through the
 * palette based on how many tags already exist — predictable but not
 * monotonic.
 */
function pickNextColor(existing: ItemTag[]): ItemTagColor {
  return ITEM_TAG_COLORS[existing.length % ITEM_TAG_COLORS.length];
}
